import { useRef } from 'react';
import { combineKeyframes } from '../utils/combineKeyframes';
import type { Keyframes } from '../components/AnimatedWrapper/AnimatedWrapper';

/**
 * Resolves a `Keyframes | Keyframes[]` prop into a single stable `Keyframes`
 * object, avoiding unnecessary re-computation when an inline array literal
 * (e.g. `[slideInLeft, fadeIn]`) is passed as a prop.
 *
 * React creates a new array reference on every render for inline literals.
 * A plain `useMemo([animationProp])` would recompute each time because
 * `Object.is` compares by reference.  This hook instead compares array
 * *elements* by identity — the individual keyframe tokens are module-level
 * constants whose references never change — and only calls
 * `combineKeyframes` when the elements actually differ.
 */
export function useStableAnimation(
  animationProp: Keyframes | Keyframes[] | undefined
): Keyframes | undefined {
  const prevInputRef = useRef<Keyframes | Keyframes[] | undefined>();
  const resolvedRef = useRef<Keyframes | undefined>();

  const prev = prevInputRef.current;

  const isSame = (() => {
    if (animationProp === prev) {
      return true;
    }
    if (Array.isArray(animationProp) && Array.isArray(prev)) {
      return (
        animationProp.length === prev.length &&
        animationProp.every((kf, i) => kf === prev[i])
      );
    }
    return false;
  })();

  if (!isSame) {
    resolvedRef.current = Array.isArray(animationProp)
      ? combineKeyframes(...animationProp)
      : animationProp;
    prevInputRef.current = animationProp;
  }

  return resolvedRef.current;
}
